import React, { useMemo, useState } from "react";
import {
  Typography,
  Grid,
  IconButton,
  Chip,
  Box,
  Card,
  CardContent,
  CardHeader,
  Tooltip,
  Divider,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  AccountCircle as ProspectIcon,
  Storage as StorageIcon,
  Transform as TransformIcon,
  SortByAlpha as SortByAlphaIcon,
} from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

const CreateProspectMapper = ({
  availableIngestionFields,
  prospectSchemaFields,
  prospectArrayFields,
  prospectMappings,
  arrayDefaults,
  onDragEnd,
  onAddDefault,
  onRemoveArrayDefault,
  onDoubleClick,
}) => {
  const [sortAsc, setSortAsc] = useState(true);
  const sortedAvailable = useMemo(() => {
    const arr = [...availableIngestionFields].sort((a, b) => a.localeCompare(b));
    return sortAsc ? arr : arr.reverse();
  }, [availableIngestionFields, sortAsc]);
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
        Create Prospect Mapper
      </Typography>
      <Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
        Map ingestion fields to prospect schema fields using drag-and-drop. Array fields support
        default values and custom object templates for complex data structures.
      </Typography>
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardHeader
          avatar={<TransformIcon color="primary" />}
          title="Prospect Data Mapping"
          subheader="Drag and drop fields to configure prospect creation workflows"
        />
        <Divider />
        <CardContent sx={{ p: 0 }}>
          <DragDropContext onDragEnd={onDragEnd}>
            <Grid container spacing={3}>
              {/* Left side - Available Ingestion Fields */}
              <Grid size={6}>
                <Card elevation={1} sx={{ height: 600, display: "flex", flexDirection: "column" }}>
                  <CardHeader
                    avatar={<StorageIcon color="info" />}
                    title="Available Ingestion Fields"
                    subheader={`${availableIngestionFields.length} fields ready for mapping`}
                    sx={{ pb: 1 }}
                    action={
                      <Tooltip title={`Sort ${sortAsc ? "A→Z" : "Z→A"}`}>
                        <IconButton onClick={() => setSortAsc((s) => !s)}>
                          <SortByAlphaIcon />
                        </IconButton>
                      </Tooltip>
                    }
                  />
                  <Divider />
                  <CardContent sx={{ p: 2, flex: 1, overflow: "auto" }}>
                    <Droppable droppableId="available-ingestion">
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          style={{
                            backgroundColor: snapshot.isDraggingOver ? "#f5f5f5" : "white",
                            minHeight: "400px",
                            padding: "8px",
                            border: "2px dashed #ccc",
                            borderRadius: "4px",
                          }}
                        >
                          {sortedAvailable.map((field, index) => (
                            <Draggable key={field} draggableId={field} index={index}>
                              {(dragProvided, dragSnapshot) => (
                                <div
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  {...dragProvided.dragHandleProps}
                                  style={{
                                    ...dragProvided.draggableProps.style,
                                    padding: "8px",
                                    margin: "4px 0",
                                    backgroundColor: dragSnapshot.isDragging
                                      ? "#e3f2fd"
                                      : "#f9f9f9",
                                    border: "1px solid #ddd",
                                    borderRadius: "4px",
                                    cursor: "grab",
                                  }}
                                >
                                  <Typography variant="body2" fontWeight="bold">
                                    {field}
                                  </Typography>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </CardContent>
                </Card>
              </Grid>

              {/* Right side - Prospect Schema Fields */}
              <Grid size={6}>
                <Card elevation={1} sx={{ height: 600, display: "flex", flexDirection: "column" }}>
                  <CardHeader
                    avatar={<ProspectIcon color="success" />}
                    title="Prospect Schema Fields"
                    subheader={`${prospectSchemaFields.length} target fields available`}
                    sx={{ pb: 1 }}
                  />
                  <Divider />
                  <CardContent sx={{ p: 2, flex: 1, overflow: "auto" }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {prospectSchemaFields.map((field) => {
                        const isArrayField = prospectArrayFields.includes(field);
                        const mappings = prospectMappings.filter((m) => m.prospectField === field);

                        return (
                          <div key={field}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                              {field}{" "}
                              {isArrayField && <Chip size="small" label="Array" color="primary" />}
                              {isArrayField &&
                                [
                                  "consents",
                                  "commPreferences",
                                  "legalAcceptances",
                                  "enrichmentData",
                                ].includes(field) && (
                                  <IconButton
                                    size="small"
                                    onClick={() => onAddDefault(field)}
                                    sx={{ ml: 1 }}
                                  >
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                )}
                            </Typography>
                            <Droppable droppableId={field}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.droppableProps}
                                  style={{
                                    backgroundColor: snapshot.isDraggingOver
                                      ? isArrayField
                                        ? "#fff3e0"
                                        : "#e8f5e9"
                                      : "#fafafa",
                                    minHeight: isArrayField ? "80px" : "40px",
                                    padding: "8px",
                                    border: `2px dashed ${isArrayField ? "#ff9800" : "#4caf50"}`,
                                    borderRadius: "4px",
                                    marginBottom: "8px",
                                  }}
                                >
                                  {mappings.map((mapping, index) => (
                                    <Draggable
                                      key={`${mapping.prospectField}-${mapping.ingestionField}`}
                                      draggableId={mapping.ingestionField}
                                      index={index}
                                    >
                                      {(mappingProvided, mappingSnapshot) => (
                                        <div
                                          ref={mappingProvided.innerRef}
                                          {...mappingProvided.draggableProps}
                                          {...mappingProvided.dragHandleProps}
                                          style={{
                                            ...mappingProvided.draggableProps.style,
                                            padding: "4px 8px",
                                            margin: "2px 0",
                                            backgroundColor: mappingSnapshot.isDragging
                                              ? "#e3f2fd"
                                              : "#e1f5fe",
                                            border: "1px solid #01579b",
                                            borderRadius: "4px",
                                            fontSize: "12px",
                                            cursor: "pointer",
                                          }}
                                          onDoubleClick={() => onDoubleClick(field, mapping)}
                                        >
                                          <Typography variant="caption">
                                            {mapping.ingestionField}
                                            {mapping.isArray && (
                                              <Chip
                                                size="small"
                                                label="→ Array Object"
                                                sx={{ ml: 1, fontSize: "10px" }}
                                              />
                                            )}
                                            {field === "commPreferences" && mapping.channel && (
                                              <Chip
                                                size="small"
                                                label={`Channel: ${mapping.channel}`}
                                                sx={{ ml: 1, fontSize: "10px" }}
                                                color="primary"
                                              />
                                            )}
                                          </Typography>
                                        </div>
                                      )}
                                    </Draggable>
                                  ))}

                                  {/* Display default values for array fields */}
                                  {isArrayField &&
                                    arrayDefaults[field]?.map((defaultItem, index) => (
                                      <div
                                        key={`default-${field}-${defaultItem.value}`}
                                        style={{
                                          padding: "4px 8px",
                                          margin: "2px 0",
                                          backgroundColor: "#f3e5f5",
                                          border: "1px solid #9c27b0",
                                          borderRadius: "4px",
                                          fontSize: "12px",
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                        }}
                                      >
                                        <Typography variant="caption">
                                          {defaultItem.value} (Default)
                                        </Typography>
                                        <IconButton
                                          size="small"
                                          onClick={() => onRemoveArrayDefault(field, index)}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </div>
                                    ))}

                                  {provided.placeholder}
                                  {mappings.length === 0 && (
                                    <Typography variant="caption" color="textSecondary">
                                      Drop {isArrayField ? "field for array object" : "field"} here
                                    </Typography>
                                  )}
                                </div>
                              )}
                            </Droppable>
                          </div>
                        );
                      })}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </DragDropContext>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CreateProspectMapper;
